/** Applied-filter chips over the table (screen 7): type icon + count + ×,
 *  the type name only in the hover tooltip; click opens the popup on that
 *  tab, × clears that type. The group is one flex item and never breaks. */

import { useState } from "react";
import {
  FILTER_TABS,
  counts as countsOf,
  type AdsManagerFilter,
  type FilterTab,
} from "@/lib/ads-manager-filter";
import { DevicesIcon, TypeIcon } from "./filter-icons";
import { SPLIT_TITLE } from "@/lib/device-split";

export function FilterChips({
  applied,
  describe,
  onOpen,
  onRemove,
  split,
}: {
  applied: AdsManagerFilter;
  describe: (tab: FilterTab) => string;
  onOpen: (tab: FilterTab) => void;
  onRemove: (tab: FilterTab) => void;
  /** «Split by devices» is on: its chip. `mutedReason` — on a tab where it
   *  doesn't apply (Ads) the chip stays, dimmed, with the reason. */
  split?: { onRemove: () => void; mutedReason?: string };
}) {
  const [tip, setTip] = useState<FilterTab | null>(null);
  const c = countsOf(applied);
  const tabs = FILTER_TABS.filter((t) => c[t] > 0);
  if (tabs.length === 0 && !split) return null;
  return (
    <div className="af-chips">
      {split && <SplitChip onRemove={split.onRemove} mutedReason={split.mutedReason} />}
      {tabs.map((t) => (
        <span key={t} className="af-chip-wrap">
          <span
            className="af-chip"
            role="button"
            tabIndex={0}
            onMouseEnter={() => setTip(t)}
            onMouseLeave={() => setTip(null)}
            onClick={() => onOpen(t)}
            onKeyDown={(e) => { if (e.key === "Enter") onOpen(t); }}
          >
            <TypeIcon tab={t} />
            <span className="af-chip-count">{c[t]}</span>
            <button
              type="button"
              className="af-chip-x"
              aria-label={`Clear ${t} filter`}
              onClick={(e) => { e.stopPropagation(); onRemove(t); }}
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M6 6l12 12" /><path d="M6 18 18 6" /></svg>
            </button>
          </span>
          {tip === t && <span className="af-chip-tip">{describe(t)}</span>}
        </span>
      ))}
    </div>
  );
}

/** The «Split by devices» chip: device icon + ×, name (or why it's muted)
 *  in the tooltip. Also used alone on the tabs that have no other filter
 *  (Search terms, Ads). */
export function SplitChip({
  onRemove,
  mutedReason,
}: {
  onRemove: () => void;
  mutedReason?: string;
}) {
  const [tip, setTip] = useState(false);
  return (
    <span className="af-chip-wrap">
      <span
        className={`af-chip${mutedReason ? " af-chip-muted" : ""}`}
        onMouseEnter={() => setTip(true)}
        onMouseLeave={() => setTip(false)}
      >
        <DevicesIcon />
        <button
          type="button"
          className="af-chip-x"
          aria-label="Turn off Split by devices"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M6 6l12 12" /><path d="M6 18 18 6" /></svg>
        </button>
      </span>
      {tip && <span className="af-chip-tip">{mutedReason ?? SPLIT_TITLE}</span>}
    </span>
  );
}
